import api from "./apiService";
import { parseConsistentApiResponse } from "../utils/consistentApiParser";

const sourceService = {
  // Get all sources
  getAll: async () => {
    // Silent operation

    const response = await api.get("/sources");

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, "sourceService", "getAll");

    // Return the data directly (should be an array of sources)
    return apiResponse.data || [];
  },

  // Get source by ID
  getById: async (id) => {
    const response = await api.get(`/sources/${id}`);

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, "sourceService", "getById");

    // Return the data directly (should be a single source object)
    return apiResponse.data;
  },

  // Create new source
  create: async (sourceData) => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("sourceService: Creating source with data:", sourceData);
      }

      // Create a copy of the sourceData to avoid modifying the original
      const sourceDataToSend = { ...sourceData };

      const response = await api.post("/sources", sourceDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, "sourceService", "create");

      // Return the data directly (should be the created source object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error("sourceService: Error creating source");

      if (process.env.NODE_ENV === "development") {
        console.error("sourceService: Error details:", error);
        console.error("sourceService: Error response data:", error.response?.data);
        console.error("sourceService: Error status:", error.response?.status);
      }

      // Rethrow with more detailed error message
      if (error.response?.data) {
        const errorMessage =
          typeof error.response.data === "object" ? JSON.stringify(error.response.data) : error.response.data;
        throw new Error(`API Error (${error.response.status}): ${errorMessage}`);
      }
      throw error;
    }
  },

  // Update source
  update: async (id, sourceData) => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("sourceService: Updating source with ID:", id, "and data:", sourceData);
      }

      // Create a copy of the sourceData to avoid modifying the original
      const sourceDataToSend = { ...sourceData };

      const response = await api.put(`/sources/${id}`, sourceDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, "sourceService", "update");

      // Return the data directly (should be the updated source object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error("sourceService: Error updating source");

      if (process.env.NODE_ENV === "development") {
        console.error("sourceService: Error details:", error);
        console.error("sourceService: Error response data:", error.response?.data);
      }

      throw error;
    }
  },

  // Delete source
  remove: async (id) => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("sourceService: Deleting source with ID:", id);
      }

      const response = await api.delete(`/sources/${id}`);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, "sourceService", "remove");

      // For delete operations, return the full response (contains success message)
      // But for backward compatibility, return the ID if successful
      if (apiResponse.success) {
        return id;
      }

      return apiResponse;
    } catch (error) {
      // Always log errors, even in production
      console.error("sourceService: Error deleting source");

      if (process.env.NODE_ENV === "development") {
        console.error("sourceService: Error details:", error);
        console.error("sourceService: Error response data:", error.response?.data);
      }

      throw error;
    }
  },

  // Get all users with access to a source (ADMIN only)
  getSourceUsers: async (sourceId) => {
    const response = await api.get(`/sources/${sourceId}/users`);
    const apiResponse = parseConsistentApiResponse(response, "sourceService", "getSourceUsers");
    return apiResponse.data || [];
  },

  // Grant/update a user's access to a source (ADMIN only)
  setSourceUserAccess: async (sourceId, userId) => {
    const response = await api.put(`/sources/${sourceId}/users/${userId}`);
    const apiResponse = parseConsistentApiResponse(response, "sourceService", "setSourceUserAccess");
    return apiResponse.data;
  },

  // Remove a user's access to a source (ADMIN only)
  removeSourceUserAccess: async (sourceId, userId) => {
    const response = await api.delete(`/sources/${sourceId}/users/${userId}`);
    const apiResponse = parseConsistentApiResponse(response, "sourceService", "removeSourceUserAccess");
    return apiResponse.success ? userId : apiResponse;
  },
};

export default sourceService;
